/* ──────────────────────────────────────────────────────────────
   Informe de FORMACIONES  –  4 pestañas
   ────────────────────────────────────────────────────────────── */
import { useState } from "react";
import dayjs from "dayjs";
import {
  Stack,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  LinearProgress,
  Typography,
  Alert,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  useTrainingTotalReport,
  useTrainingPorTipoReport,
  useTrainingImpartidasReport,
  useTrainingRecibidasReport,
} from "../../../hooks/useReportJob";

import {
  TrainingTotalResult,
  TrainingPorTipoResult,
  TrainingImpartidasResult,
  TrainingRecibidasResult,
} from "../../../api/reports";

import { useCentros } from "../../../hooks/useCentros";
import { useTipos } from "../../../hooks/useTiposAsignatura";
import { downloadReportFile } from "../../../components/utils/downloadXlsx";

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function FormacionesPage() {
  const { t } = useTranslation<"translation">();
  /* ── filtros comunes ─────────────────────────────────────────────────── */
  const [desde, setDesde] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [hasta, setHasta] = useState(dayjs().endOf("month").format("YYYY-MM-DD"));
  const [centroId, setCentro] = useState<number | "">("");
  const [tipoId, setTipo] = useState<number | "">("");

  const { rows: centros } = useCentros(); // [{ id, nombre_centro }]
  const { rows: tipos } = useTipos(); // [{ id, descripcion  }]

  /* ── pestaña activa ──────────────────────────────────────────────────── */
  const [tab, setTab] = useState(0);

  /* ── hooks para cada informe (run + polling) ─────────────────────────── */
  const { run: runTot, useStatus: useStatTot } = useTrainingTotalReport();
  const { run: runTipo, useStatus: useStatTipo } = useTrainingPorTipoReport();
  const { run: runImp, useStatus: useStatImp } = useTrainingImpartidasReport();
  const { run: runRec, useStatus: useStatRec } = useTrainingRecibidasReport();

  /* jobIds individuales — 1 por pestaña — para poder refrescar cada una */
  const [jobTot, setJobTot] = useState<string>();
  const [jobTipo, setJobTipo] = useState<string>();
  const [jobImp, setJobImp] = useState<string>();
  const [jobRec, setJobRec] = useState<string>();

  /* polling: los hooks SIEMPRE se llaman, pero quedan disabled sin jobId */
  const statTot = useStatTot(jobTot);
  const statTipo = useStatTipo(jobTipo);
  const statImp = useStatImp(jobImp);
  const statRec = useStatRec(jobRec);

  /* status que se mostrará según pestaña */
  const status =
    tab === 0 ? statTot.data : tab === 1 ? statTipo.data : tab === 2 ? statImp.data : statRec.data;

  const pendiente = !!status && !status.done && !status.failed;

  /* payload base */
  const basePayload = {
    desde,
    hasta,
    centroId: centroId || undefined,
    tipoId: tipoId || undefined,
  };

  /* ── disparar generación ─────────────────────────────────────────────── */
  const generar = async () => {
    switch (tab) {
      case 0: {
        const { jobId } = await runTot(basePayload);
        setJobTot(jobId);
        break;
      }
      case 1: {
        const { jobId } = await runTipo(basePayload);
        setJobTipo(jobId);
        break;
      }
      case 2: {
        const { jobId } = await runImp(basePayload);
        setJobImp(jobId);
        break;
      }
      case 3: {
        const { jobId } = await runRec(basePayload);
        setJobRec(jobId);
        break;
      }
    }
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  /* ── render tabla / panel según pestaña ─────────────────────────────── */
  let panel: JSX.Element | null = null;

  if (status?.done && status.result) {
    switch (tab) {
      /* 0 ▸ total sesiones / horas ------------------------------------- */
      case 0: {
        const r = status.result as TrainingTotalResult;
        panel = (
          <Paper sx={{ p: 2 }}>
            <Typography>
              {t("formaciones.panelTotal.sesiones")}: <b>{r.formaciones}</b>
            </Typography>
            <Typography>
              {t("formaciones.panelTotal.horas")}:<b>{r.horas}</b>
            </Typography>
          </Paper>
        );
        break;
      }

      /* 1 ▸ por tipo ---------------------------------------------------- */
      case 1: {
        const rows = (status.result as TrainingPorTipoResult).rows;
        panel = (
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("formaciones.panelTipo.tipo")}</TableCell>
                  <TableCell align="right">{t("formaciones.panelTipo.sesiones")}</TableCell>
                  <TableCell align="right">{t("formaciones.panelTipo.horas")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.tipoId}>
                    <TableCell>{r.tipo}</TableCell>
                    <TableCell align="right">{r.formaciones}</TableCell>
                    <TableCell align="right">{r.horas}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        );
        break;
      }

      /* 2 ▸ impartidas  ·  3 ▸ recibidas ------------------------------- */
      default: {
        const rows = (status.result as TrainingImpartidasResult | TrainingRecibidasResult).rows;
        panel = (
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("formaciones.panelProf.profesor")}</TableCell>
                  <TableCell align="right">{t("formaciones.panelProf.sesiones")}</TableCell>
                  <TableCell align="right">{t("formaciones.panelProf.horas")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.profesorId}>
                    <TableCell>{r.profesor}</TableCell>
                    <TableCell align="right">{r.formaciones}</TableCell>
                    <TableCell align="right">{r.horas}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        );
      }
    }
  }

  /* ── UI ─────────────────────────────────────────────────────────────── */
  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t("formaciones.titulo")}</Typography>

      {/* filtros */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          type="date"
          label={t("formaciones.filtros.desde")}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />
        <TextField
          type="date"
          label={t("formaciones.filtros.hasta")}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>{t("formaciones.filtros.centro")}</InputLabel>
          <Select
            label="Centro"
            value={centroId}
            onChange={(e) => setCentro(e.target.value as any)}
          >
            {centros.map((c) => (
              <MenuItem key={c.id} value={c.id || ""}>
                {c.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>{t("formaciones.filtros.tipoProfesor")}</InputLabel>
          <Select
            label="Tipo prof."
            value={tipoId}
            onChange={(e) => setTipo(e.target.value as any)}
          >
            {tipos.map((t) => (
              <MenuItem key={t.id} value={t.id || ""}>
                {t.descripcion}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="contained" onClick={generar}>
          {t("formaciones.filtros.generar")}
        </Button>
      </Stack>

      {/* pestañas */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label={t("formaciones.tabs.total")} />
        <Tab label={t("formaciones.tabs.porTipo")} />
        <Tab label={t("formaciones.tabs.impartidas")} />
        <Tab label={t("formaciones.tabs.recibidas")} />
      </Tabs>

      {/* progreso y link XLSX */}
      {pendiente && <LinearProgress variant="determinate" value={status!.progress} />}
      {status?.done && status.result?.url && (
        <Alert severity="success" sx={{ width: "fit-content" }}>
          {t("formaciones.estado.exito")}{" "}
          <Button
            variant="text"
            size="small"
            onClick={handleDownload}
            sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
          >
            {t("formaciones.estado.descargar")}
          </Button>
        </Alert>
      )}

      {panel}
    </Stack>
  );
}
